import { type HTMLProps, type ReactElement, useEffect, useState } from "react";
import { t } from "@lingui/core/macro";
import styled from "styled-components";

import { Icon } from "_/components/icon";

import * as S from "./styled";

type MachineCameraMediaProps = {
  /** Full size image URL.*/
  src: string;

  /**
   * Scaled down thumbnail URL.
   *
   * If this is not provided, the main image source will be used for
   * the scaled down thumbnail preview.
   */
  thumb?: string;

  /**
   * Optional video URL. If present, show a video-play button on the thumbnail,
   * and open the video when clicked.
   */
  video?: string;

  /**
   * Optional video download URL. If present, show a download button.
   */
  videoDownload?: string;

  /** Called with the preview URL after it finishes loading. */
  onThumbnailLoad?: (src: string) => void;

  /**
   * Allows callers to change the displayed image in response to keyboard arrow
   * keys.
   *
   * The value of `delta` will be:
   * - 1: right arrow or down arrow
   * - -1: left arrow or up arrow
   *
   * Values are multiplied by 10 if the Shift key is pressed.
   *
   * We may extend this capability in the future to support other ways of
   * changing the image, such as buttons on the UI, mouse wheel, touch gestures,
   * a slider bar, etc. This may require a different interface that supports
   * absolute or relative navigation, but we can address those needs if/when
   * they arise.
   */
  onIncrementImage?: (delta: number) => void;
} & HTMLProps<HTMLDivElement>;

export const MachineCameraMedia = styled(function MachineCameraMedia({
  src,
  thumb,
  style,
  className,
  video,
  videoDownload,
  onThumbnailLoad,
  onIncrementImage,
}: MachineCameraMediaProps): ReactElement {
  const [expanded, setExpanded] = useState<"none" | "image" | "video">("none");
  const [hovered, setHovered] = useState(false);
  const [imageErrors, setImageErrors] = useState({
    src,
    thumb,
    thumbnailFailed: false,
    fullSizeFailed: false,
  });

  // Reset errors before rendering a new selection, keeping the same image elements.
  if (imageErrors.src !== src || imageErrors.thumb !== thumb) {
    setImageErrors({
      src,
      thumb,
      thumbnailFailed: false,
      fullSizeFailed: false,
    });
  }

  const load = hovered || expanded !== "none";
  const thumbnailSrc = imageErrors.thumbnailFailed ? src : (thumb ?? src);
  const thumbnailUnavailable =
    thumbnailSrc === src && imageErrors.fullSizeFailed;

  function handleThumbnailError() {
    if (thumbnailSrc === src) {
      setImageErrors({ ...imageErrors, fullSizeFailed: true });
    } else {
      setImageErrors({ ...imageErrors, thumbnailFailed: true });
    }
  }

  const handleFullSizeError = () =>
    setImageErrors({ ...imageErrors, fullSizeFailed: true });

  function setShade(event: React.MouseEvent, mode: typeof expanded) {
    event.stopPropagation();
    setExpanded(mode);
  }

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setExpanded("none");
      } else if (
        expanded === "image" &&
        onIncrementImage &&
        (e.key === "ArrowRight" ||
          e.key === "ArrowDown" ||
          e.key === "ArrowLeft" ||
          e.key === "ArrowUp")
      ) {
        const direction =
          e.key === "ArrowRight" || e.key === "ArrowDown" ? 1 : -1;
        onIncrementImage(direction * (e.shiftKey ? 10 : 1));
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [expanded, onIncrementImage]);

  const thumbnailImage = thumbnailUnavailable ? (
    <S.UnavailableImageMessage>{t`common.no-image-available`}</S.UnavailableImageMessage>
  ) : (
    <S.ThumbnailImage
      src={thumbnailSrc}
      alt={t`components.machine-camera-media.camera-image`}
      onLoad={() => onThumbnailLoad?.(thumbnailSrc)}
      onError={handleThumbnailError}
    />
  );

  const fullSizeImage = imageErrors.fullSizeFailed ? (
    <S.UnavailableImageMessage>{t`common.no-image-available`}</S.UnavailableImageMessage>
  ) : (
    <S.FullSizeImage src={src} onError={handleFullSizeError} />
  );

  // Mounting the hidden image on hover starts its download before expansion.
  const shade = load ? (
    <S.Shade
      onClick={(e) => setShade(e, "none")}
      $visible={expanded !== "none"}
    >
      {expanded === "video" ? (
        <S.VideoWrapper>
          <S.FullSizeVideo
            src={video}
            autoPlay
            controls
            controlsList="nodownload"
            onClick={(e) => e.stopPropagation()}
          />
          <S.FullSizeButtonContainer>
            {videoDownload && (
              <S.DownloadButton
                href={videoDownload}
                onClick={(e) => e.stopPropagation()}
              >
                <Icon variant="FileDownload" />
              </S.DownloadButton>
            )}
            <S.CloseButton onClick={(e) => setShade(e, "none")}>
              <Icon variant="Close" />
            </S.CloseButton>
          </S.FullSizeButtonContainer>
        </S.VideoWrapper>
      ) : (
        <S.FullSizeImageWrapper>
          {fullSizeImage}
          <S.FullSizeButtonContainer>
            <S.CloseButton onClick={(e) => setShade(e, "none")}>
              <Icon variant="Close" />
            </S.CloseButton>
          </S.FullSizeButtonContainer>
        </S.FullSizeImageWrapper>
      )}
    </S.Shade>
  ) : null;

  return (
    <S.Thumbnail
      className={className}
      style={style}
      onClick={(e) => setShade(e, "image")}
      onMouseOver={() => setHovered(true)}
    >
      {thumbnailImage}
      {shade}
      {expanded === "none" && (
        <>
          {!thumbnailUnavailable && (
            <S.ZoomButton onClick={(e) => setShade(e, "image")}>
              <Icon variant="ZoomIn" />
            </S.ZoomButton>
          )}
          {video && (
            <S.PlayVideoButton
              onClick={(e) => {
                e.stopPropagation();
                setShade(e, "video");
              }}
            >
              <Icon variant="Video" />
            </S.PlayVideoButton>
          )}
        </>
      )}
    </S.Thumbnail>
  );
})``;
